import React, { Component } from 'react';
import { defaultNotifier } from 'functions/notificationHandler';
import Card from 'components/Detail/Card';
import { isEqual } from 'lodash';
import {
  DEFAULT_MAP_CHART_CONFIG,
  getMapChartConfig,
  getCartesianChartConfig,
  getActualTimeRange,
  getChartRenderRequirements,
  chartDotMenu
} from './utils'
import classNames from 'classnames';
import ChartDotMenu from './ChartDotMenu';
import { ChartLoader } from './ChartLoader';
import { rawChartData, previewRawChartData } from '../../../../../api/dashboard';
import { CHART_DEFAULT_VALUES } from '../chartConfigurationsV2'
import { BLOCK, WARN } from './constants';
import { VuiGoogleMap, VuiButton, VuiFullScreen } from '@topkamera/viso-ui';
import { getDisplayedData } from './utils'

class MapChart extends Component {
  constructor(props) {
    super(props);
    this.state = {
      chartData: {
        groupBy: [],
        data: []
      },
      isFullScreen: false,
      metricColumns: [],
      validData: false,
      dataErrorMessage: '',
      dataWarnMessage: '',
      mapConfig: { ...DEFAULT_MAP_CHART_CONFIG },
      isChartDataLoading: false,
      chartConfig: { ...CHART_DEFAULT_VALUES }
    }
  }

  // eslint-disable-next-line
  componentWillReceiveProps(nextprops) {
    if (!isEqual(this.props.isDashboardInitialized, nextprops.isDashboardInitialized)) {
      if (nextprops.isPreview) {
        return this.setPreviewData()
      }
      if (nextprops.isDashboardInitialized) {
        return this.setData()
      }
    }

    if (nextprops.errors) {
      defaultNotifier(nextprops.errors, this.props);
    }
  }

  async componentDidMount() {
    if (this.props.isPreview) {
      this.setState({ chartConfig: this.props.chartValues }, () => {
        this.setPreviewData()
      })
    } else {
      const chartConfig = JSON.parse(this.props.chart?.configuration)
      this.setState({ chartConfig: chartConfig })
    }
  }

  async componentDidUpdate(prevProps) {
    if (this.props.isPreview) {
      const currentChartValues = this.props.chartValues
      const prevChartValues = this.state.chartConfig

      const currentDataStreams = this.props.dataStreams
      const prevDataStreams = prevProps.dataStreams
      const currentChart = this.props.chart
      const prevChart = prevProps.chart
      const currentPreviewParams = this.props.previewParams
      const prevPreviewParams = prevProps.previewParams

      if (!isEqual(this.props.chart?.type, prevProps.chart?.type)) {
        return this.setPreviewData()
      }

      if (!isEqual(this.props.refreshPreview, prevProps.refreshPreview)) {
        return this.setPreviewData()
      }

      if (!isEqual(currentDataStreams, prevDataStreams)) {
        return this.setPreviewData()
      }

      if (!isEqual(currentChartValues, prevChartValues)) {
        this.setState({ chartConfig: { ...currentChartValues } }, () => {
          if (!isEqual(currentChartValues.devices, prevChartValues.devices) ||
            !isEqual(currentChartValues.groupBy, prevChartValues.groupBy)) {
            return this.setPreviewData()
          }
        })
      }

      if (!isEqual(prevProps.devicesData, this.props.devicesData)) {
        return this.setPreviewData()
      }

      if (!isEqual(currentChart, prevChart) || !isEqual(currentPreviewParams, prevPreviewParams)) {
        return this.setPreviewData()
      }

      if (this.props.refreshPreview && !this.props.isNewVersion) {
        return this.setState({
          cartesianConfig: getCartesianChartConfig(this.props.chart.configuration)
        })
      }
    }

    if (!isEqual(this.props.devicesData, prevProps.devicesData)) {
      return this.setPreviewData()
    }

    const prevIsDashboardInitialized = prevProps.isDashboardInitialized
    const actualIsDashboardInitialized = this.props.isDashboardInitialized
    if ((prevIsDashboardInitialized !== actualIsDashboardInitialized) && actualIsDashboardInitialized) {
      this.setData()
    }
  }

  setPreviewData = async () => {
    try {
      this.setRawChartData()
    } catch (error) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  setData = async () => {
    const { chart, dashboardTimeRange } = this.props
    this.setState({ isChartDataLoading: true })

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange)

    try {
      const { hasBlockingIssues } = this.checkChartRenderRequirements(actualTimeRange)

      if (hasBlockingIssues) {
        return this.setState({ isChartDataLoading: false })
      }
      this.setRawChartData()
    } catch (error) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: error.message,
        validData: false
      })
    }
  }

  checkChartRenderRequirements = (timeRange) => {
    const requirements = getChartRenderRequirements(this.state.chartConfig, timeRange)

    const warnMessages = requirements[WARN].map(r => r.message).join('\n')
    const blockMessages = requirements[BLOCK].map(r => r.message).join('\n')
    const hasWarningIssues = requirements[BLOCK].length > 0
    const hasBlockingIssues = requirements[BLOCK].length > 0

    if (hasWarningIssues) {
      this.setState({ dataWarnMessage: warnMessages })
    }

    if (hasBlockingIssues) {
      this.setState({
        validData: false,
        dataErrorMessage: blockMessages,
        chartData: { groupBy: [], data: [] }
      })
    }

    return {
      hasBlockingIssues
    }
  }

  setChartData = (data, chart) => {
    const { groupBy, metricsData, metricColumns } = data
    const mapConfig = getMapChartConfig(chart.configuration)
    const chartData = {
      groupBy: [...groupBy],
      data: [...metricsData]
    }

    return this.setState({
      chartData: chartData,
      metricColumns: metricColumns,
      mapConfig: mapConfig,
      dataErrorMessage: '',
      validData: true
    })
  }

  mapMarkerData = () => {
    const { locations } = this.props
    const { chartData, metricColumns, chartConfig } = this.state

    const hasData = Array.isArray(chartData?.data) && chartData.data.length > 0
    const hasMetrics = Array.isArray(metricColumns) && metricColumns.length >= 2
    if (!hasData || !hasMetrics) {
      return []
    }

    const getKey = metricColumns[0]
    const valueKey = metricColumns[1]
    let buildMarkers = []

    chartData?.data?.forEach(item => {
      const deviceName = item?.[getKey]
      if (!deviceName) {
        return
      }
      const findLocation = locations.find(location => location.devices.find(device => device.name === deviceName))
      const locationCordinates = findLocation?.coordinates
      const markerLabels = chartConfig?.metric?.metrics || []
      const colorTiers = chartConfig?.colorTiers?.customConfigs || []
      const getValue = item?.[valueKey]
      let customColor = chartConfig?.dataStreamCard?.streamLabel?.color
      let customLabel = ''

      if (chartConfig?.enableThreshold) {
        const findColorTier = (colorTiers, value) => {
          if (!Array.isArray(colorTiers) || colorTiers.length === 0) {
            return undefined
          }
          return colorTiers.reduce((best, tier) => {
            if (value < tier.value) {
              return best
            }
            if (!best || tier.value > best.value) {
              return tier
            }
            return best
          }, undefined)
        }
        const tier = findColorTier(colorTiers, Number(getValue))
        if (tier) {
          customColor = tier.color
          customLabel = tier.text || ''
        }
      }

      let tooltipPayload = [
        {
          hasDivider: true,
          label: 'Device',
          value: deviceName
        },
        {
          label: 'Location',
          value: findLocation?.name || 'Unknown'
        }
      ]

      markerLabels.length > 0 && markerLabels.forEach(label => {
        tooltipPayload.push({
          color: customColor,
          label: customLabel || chartConfig?.dataStreamCard?.streamLabel?.value || label,
          value: getDisplayedData({ chartData, chartConfig, value: getValue }) || '-'
        })
      })

      locationCordinates && buildMarkers.push({
        label: deviceName,
        position: {
          lat: Number(locationCordinates.lat),
          lng: Number(locationCordinates.lng)
        },
        customColor: customColor,
        payload: tooltipPayload
      })
    })

    return buildMarkers
  }

  getDeviceList = () => {
    const { chartValues, devicesData } = this.props

    if (chartValues?.devices && !!chartValues.devices.length) {
      return chartValues.devices
    }

    return devicesData?.map(item => item.name)
  }

  setRawChartData = async () => {
    const { chart, dashboardTimeRange, isPreview, chartValues, isDefaultTime } = this.props
    const { chartConfig } = this.state

    const actualTimeRange = getActualTimeRange(chart, dashboardTimeRange, isPreview, true, chartConfig)

    let groupByMetrics = chartConfig?.groupBy?.metrics || []

    if (this.props.isPreview) {
      groupByMetrics = chartValues?.groupBy?.metrics || []
    }

    this.setState({ isChartDataLoading: true })

    if (!chartConfig?.dataStreamCard?.metric?.metrics.length) {
      if (!this.props.isPreview) {
        return
      }
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: "Select a metric to display the preview",
        validData: false,
        isChartDataLoading: false
      })
    }

    if (this.props.sqlError) {
      return this.setState({
        chartData: {
          groupBy: [],
          data: []
        },
        dataErrorMessage: this.props.sqlError,
        validData: false
      })
    }

    this.setState({ isChartDataLoading: true })

    let data = {}

    const devices = this.getDeviceList();

    if (!devices.length) {
      return this.setState({ isChartDataLoading: false })
    }

    if (this.props.isPreview) {
      const response = await previewRawChartData({
        dataStreams: this.props.dataStreams,
        visoDevices: devices,
        dashboardId: this.props.dashboardId
      })

      data = {
        errors: response?.data?.errors || null,
        data: response?.data?.data?.previewRawChartData
      }
      this.setState({ isChartDataLoading: false })
    } else {
      this.setState({ isChartDataLoading: true })

      if (devices.length) {
        if (isDefaultTime) {
          const response = await rawChartData({
            chartId: this.props.chart?.id,
            visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
            timeRange: { start: actualTimeRange.start, end: actualTimeRange.end }
          })
          this.setState({ isChartDataLoading: false })

          data = {
            errors: response?.data?.errors || null,
            data: response?.data?.data?.rawChartData
          }
        } else {
          const response = await rawChartData({
            chartId: this.props.chart?.id,
            visoDevices: this.props.deviceIds?.length ? this.props.deviceIds : devices,
          })
          this.setState({ isChartDataLoading: false })

          data = {
            errors: response?.data?.errors || null,
            data: response?.data?.data?.rawChartData
          }
        }

      }
    }

    if (data.errors) {
      const errorMessage = data.errors?.[0]?.message
      return this.setState({
        dataErrorMessage: errorMessage,
        validData: false,
        isChartDataLoading: false
      })
    }

    if (data.data) {

      const rawData = data.data.flatMap(item => item.result.data)
      const rawFields = data.data.flatMap(item => item.result.fields)

      const chartData = rawData

      this.setState({
        chartData: {
          groupBy: [],
          data: chartData,
          uniqueGroupValues: []
        },
        metricColumns: rawFields,
        dataErrorMessage: '',
        validData: true
      })
    }
  }

  renderContent = () => {
    const { validData, dataErrorMessage, dataWarnMessage, chartConfig, isFullScreen } = this.state;
    const { chart, height, isPreview } = this.props
    const streamData = this.mapMarkerData()
    const positions = streamData.map(marker => marker.position).filter(Boolean)
    const uniquePositionsCount = new Set(positions.map(position => `${position.lat},${position.lng}`)).size
    const hasMultiplePositions = uniquePositionsCount > 1
    const singlePosition = uniquePositionsCount === 1 ? positions[0] : undefined

    const renderMap = () => {
      return (
        <div
          className='charts-map-chart'
          onMouseDown={e => e.stopPropagation()}
          onTouchStart={e => e.stopPropagation()}
        >
          <VuiGoogleMap
            popOverId='popoverRoot'
            apiKey={process.env.GOOGLE_MAPS_API_KEY}
            mapId={chart?.id || 'previewMapChart'}
            markers={streamData}
            hasClusters={true}
            fitToMarkers={hasMultiplePositions}
            defaultCenter={singlePosition}
            defaultZoom={10}
            hasChartTooltip
            fitPadding={50}
            defaultMode={chartConfig?.mapType || 'roadmap'}
            showLabelsOnHover={!chartConfig?.enableMapLabels || false}
            height={isFullScreen ? 'calc(100vh - 298px)' : (isPreview && height)}
          />
        </div>
      )
    }

    return (
      <React.Fragment>
        {!validData ?
          <div className='charts-card-chart-content'>
            <div className='chart-error'>
              <div>
                {dataErrorMessage}
              </div>
              <div>
                {dataWarnMessage}
              </div>
            </div>
          </div>
          :
          this.state.isFullScreen ?
            <VuiFullScreen
              elementId='fullscreenRoot'
              onClose={() => this.setState({ isFullScreen: false })}
            >
              {renderMap()}
            </VuiFullScreen>
            :
            renderMap()
        }
      </React.Fragment>
    );
  }

  render() {
    const { color, chart, isLegacy, isEditMode } = this.props;

    return (
      <Card
        noGrid
        color={this.props.color}
        menu={!this.props.isPreview && 'dot'}
        chartId={this.props.chart.id}
        contentLoading={this.state.isChartDataLoading}
        className={classNames("overview charts-card-chart chart-card-chart-actions-button-white", {
          "charts-card-preview": this.props.isPreview
        })}
        height={this.props.isPreview && this.props.height}
        content={this.state.isChartDataLoading ? <ChartLoader color={this.props.color} /> : this.renderContent()}
        onToogleMenu={this.props.onToogleMenu}
        customMenuAction={!this.props.isPreview &&
          <div className="charts-card-chart__menu-action">
            <VuiButton
              iconLeft='expand'
              label='Expand'
              iconSize='sm'
              color='border'
              style='color'
              onClick={() => this.setState({ isFullScreen: true })}
            />
          </div>
        }
        dropDownMenu={!this.props.isPreview && this.props.onEdit && chartDotMenu({
          isLight: true,
          color,
          showAI: this.props.showAI,
          onEdit: this.props.onEdit,
          onCopy: this.props.onCopy,
          onDelete: this.props.onDelete,
          onExplainKPI: () => this.props.onExplainKPI(chart?.id),
          onImproveChart: () => this.props.onImproveChart(chart?.id),
          isEditMode
        })}
      />
    );
  }
}

export default MapChart;